import { Divider } from "react-native-paper";
import {
    Button,
    ColorPickerInput,
    Form,
    InputControl,
    Loading,
    Picker,
    Spacer,
    StyleFunction,
    TextInput,
    useForm,
    useMerchantConfig,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    ButtonDimensions,
    formatAmount,
    LayoutButtonMetaData,
    LayoutEntityButton,
    ValidationErrors,
    ValidationSchema,
} from "lib";
import React, { View } from "react-native";
import { useCallback } from "react";
import { AvailableLocale, useTranslation } from "locales";
import {
    GQBuyAccountFunds,
    GQLayoutEntitiesQuery,
    useLayoutEntitiesQuery,
} from "graphql-sdk";
import { useAdminSession } from "../../../../../session";
import { schemaButtonDimensions } from "./validation";
import { LayoutEntityModalOutput } from ".";
import { ButtonSizeForm } from "../../../button-size-form";

type LayoutEntityButtonInput = {
    entityId: string;
    color: string;
    label: string;
};

const schemaLayoutEntityButton: ValidationSchema<LayoutEntityButtonInput> = {
    entityId: { required: true },
    color: { required: true, color: true },
};

export function LayoutEntityForm({
    metaData,
    values,
    onSubmit,
}: {
    values: LayoutEntityButtonInput & ButtonDimensions;
    metaData: LayoutButtonMetaData;
    onSubmit: (values?: LayoutEntityModalOutput) => void;
}) {
    const dimensionForm = useForm<ButtonDimensions>(schemaButtonDimensions, {
        width: values.width || 1,
        height: values.height || 1,
    });
    const form = useForm<LayoutEntityButtonInput>(schemaLayoutEntityButton, {
        entityId: values.entityId || "",
        color: values.color || "",
        label: values.label || "",
    });

    const [{ values: formValues }] = form;

    const { data: layoutEntityData, loading } = useLayoutEntitiesQuery({
        variables: {
            pagination: {
                sort: "name",
                sortDirection: "ASC",
                pageSize: 999999,
            },
        },
    });

    const currentEntity = layoutEntityData?.layoutEntities.data.find(
        item => item.id === formValues.entityId
    );

    let currentConfigurationErrors: ValidationErrors<LayoutEntityButtonInput> =
        {};
    if (metaData.missingConfigurationData) {
    }

    const handleSubmit = useCallback(() => {
        if (!formValues) {
            return;
        }

        onSubmit({
            buttonType: "LAYOUT_ENTITY",
            formValues: {
                ...formValues,
                ...dimensionForm[0].values,
            } as LayoutEntityButton,

            displayValues: {
                color: formValues.color,
                label: formValues.label || currentEntity.name,
            },
        });
    }, [currentEntity, dimensionForm, formValues, onSubmit]);

    if (loading) {
        return (
            <>
                <Loading />
            </>
        );
    }

    return (
        <>
            <ButtonSizeForm metaData={metaData} form={dimensionForm} />
            <Divider />
            <Spacer space={2} />
            <EventProductForm
                form={form}
                layoutEntityData={layoutEntityData}
                configurationErrors={currentConfigurationErrors}
                onSubmit={handleSubmit}
            />
        </>
    );
}

function EventProductForm({
    form,
    layoutEntityData,
    configurationErrors,
    onSubmit,
}: {
    form: Form<LayoutEntityButtonInput>;
    layoutEntityData: GQLayoutEntitiesQuery;
    configurationErrors: Form<LayoutEntityButtonInput>[0]["errors"];
    onSubmit: () => void;
}) {
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const [{ values, errors }, { setValue, handleSubmit }] = form;
    const { currency } = useMerchantConfig();
    const [{ locale }] = useAdminSession(["locale"]);

    const translateFunction = useCallback(
        (item: GQLayoutEntitiesQuery["layoutEntities"]["data"][0]) => {
            switch (item.function) {
                case "CUSTOM_ITEM":
                    return t(
                        "layout.entity_button.types.custom_item",
                        "Custom item"
                    );
                case "BUY_ACCOUNT_FUNDS":
                    if ((item.data as GQBuyAccountFunds).amount > 0) {
                        return t(
                            "layout.entity_button.types.buy_account_funds_with_amount",
                            "Buy account funds {{amount}}",
                            {
                                amount: formatAmount(
                                    (item.data as GQBuyAccountFunds).amount,
                                    currency,
                                    {
                                        locale: locale as AvailableLocale,
                                    }
                                ),
                            }
                        );
                    } else {
                        return t(
                            "layout.entity_button.types.buy_account_funds",
                            "Buy account funds"
                        );
                    }
                default:
                    return item;
            }
        },
        [currency, locale, t]
    );

    if (!values) {
        return null;
    }

    return (
        <>
            <View style={styles.rowContainer}>
                <InputControl
                    error={errors.entityId || configurationErrors.entityId}
                    style={styles.rowContent}
                >
                    <Picker
                        label={t("layout.entity_button.eventId", "Entity")}
                        onValueChange={newValue => {
                            setValue("entityId", newValue);
                        }}
                        selectedValue={values.entityId}
                        testID="layout.modal.eventId"
                        style={
                            configurationErrors.entityId ? styles.warning : null
                        }
                    >
                        {configurationErrors.entityId ? (
                            <Picker.Item
                                label={values.entityId}
                                value={values.entityId}
                                key={`layout_entity_${values.entityId}`}
                            />
                        ) : null}
                        {layoutEntityData.layoutEntities.data.length === 0 ? (
                            <Picker.Item
                                label={t(
                                    "layout.entity_button.no_entities",
                                    "No entities"
                                )}
                                value=""
                                key="__none__"
                            />
                        ) : (
                            <Picker.Item
                                label={t(
                                    "layout.entity_button.pick_an_entity",
                                    "Pick an entity"
                                )}
                                value=""
                                key="__select__"
                            />
                        )}
                        {layoutEntityData.layoutEntities.data.map(item => (
                            <Picker.Item
                                label={`${item.name} (${translateFunction(
                                    item
                                )})`}
                                value={item.id}
                                key={`layout_entity_${item.id}`}
                            />
                        ))}
                    </Picker>
                </InputControl>
            </View>
            <View style={[styles.rowContainer, styles.onTop]}>
                <InputControl
                    error={errors.label}
                    description={t(
                        "layout.entity_button.label_description",
                        "If this field is left blank, the button will be labelled with the name of the selected event / product."
                    )}
                    style={styles.rowContent}
                >
                    <TextInput
                        label={t(
                            "layout.entity_button.label",
                            "The button's text"
                        )}
                        placeholder={t(
                            "layout.entity_button.label_placeholder",
                            "Write a short button text"
                        )}
                        value={values.label}
                        onChangeText={text => {
                            setValue("label", text);
                        }}
                        onClear={() => {
                            setValue("label", "");
                        }}
                    />
                </InputControl>
                <InputControl error={errors.color} style={styles.rowContent}>
                    <ColorPickerInput
                        defaultColor={values.color ?? undefined}
                        label={t("layout.button.color", "Button color")}
                        onChangeColor={newColor => {
                            setValue("color", newColor);
                        }}
                    />
                </InputControl>
            </View>
            <Button type="primary" onPress={handleSubmit(onSubmit)}>
                {t("common.save", "Save")}
            </Button>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    rowContent: {
        flex: 1,
    },
    rowContainer: {
        ...theme.styles.row,
        columnGap: theme.spacingScale * 2,
    },
    onTop: { zIndex: 2 },
    colorRow: {
        ...theme.styles.row,
        alignItems: "baseline",
        marginBottom: 8, // To match the height/layout of the right side of the row.
    },
    groupColor: {
        marginLeft: theme.spacingScale / 2,
    },

    warning: {
        borderColor: theme.colors.warning,
    },
});
